import {
  type DeepKeys,
  type DeepValue,
  type FormAsyncValidateOrFn,
  type FormValidateOrFn,
  type ReactFormExtendedApi,
} from "@tanstack/react-form";
import {
  type ChangeEventHandler,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { InputField } from "../../components/Input";
import { InputNote } from "../../components/Input";

type TanStackForm<TFormData> = ReactFormExtendedApi<
  TFormData,
  undefined | FormValidateOrFn<TFormData>,
  undefined | FormValidateOrFn<TFormData>,
  undefined | FormAsyncValidateOrFn<TFormData>,
  undefined | FormValidateOrFn<TFormData>,
  undefined | FormAsyncValidateOrFn<TFormData>,
  undefined | FormValidateOrFn<TFormData>,
  undefined | FormAsyncValidateOrFn<TFormData>,
  undefined | FormValidateOrFn<TFormData>,
  undefined | FormAsyncValidateOrFn<TFormData>,
  undefined | FormAsyncValidateOrFn<TFormData>,
  unknown
>;

type TanStackFormFieldRenderProps<
  TFormData,
  TName extends DeepKeys<TFormData>,
> = {
  value: DeepValue<TFormData, TName>;
  setValue: (value: DeepValue<TFormData, TName>) => void;
  onBlur: () => void;
  inputProps: {
    name: string;
    value: DeepValue<TFormData, TName> | "";
    onBlur: () => void;
  };
  numberInputProps: Pick<
    InputHTMLAttributes<HTMLInputElement>,
    "name" | "onBlur" | "value"
  > & {
    onChange: ChangeEventHandler<HTMLInputElement>;
  };
};

type TanStackFormFieldProps<TFormData, TName extends DeepKeys<TFormData>> = {
  form: TanStackForm<TFormData>;
  name: TName;
  label: ReactNode;
  className?: string;
  labelClassName?: string;
  containerClassName?: string;
  validators?: {
    onSubmit?: (props: {
      value: DeepValue<TFormData, TName>;
    }) => string | undefined;
  };
  children: (
    props: TanStackFormFieldRenderProps<TFormData, TName>,
  ) => ReactNode;
};

export const TanStackFormField = <
  TFormData,
  TName extends DeepKeys<TFormData>,
>({
  form,
  name,
  label,
  className,
  labelClassName,
  containerClassName,
  validators,
  children,
}: TanStackFormFieldProps<TFormData, TName>) => {
  return (
    <form.Field name={name} validators={validators}>
      {({ state, handleChange, handleBlur }) => {
        const nameAsString = String(name);
        const errorMessage = state.meta.errors
          .map((error) => String(error))
          .filter(Boolean)
          .join(", ");

        return (
          <InputField
            label={label}
            className={className}
            labelClassName={labelClassName}
            containerClassName={
              errorMessage
                ? `border-red-300 focus-within:border-red-500 ${containerClassName ?? ""}`
                : containerClassName
            }
            note={
              errorMessage ? (
                <InputNote type="error">{errorMessage}</InputNote>
              ) : undefined
            }
          >
            {children({
              value: state.value,
              setValue: handleChange,
              onBlur: handleBlur,
              inputProps: {
                name: nameAsString,
                value: state.value ?? "",
                onBlur: handleBlur,
              },
              numberInputProps: {
                name: nameAsString,
                value: typeof state.value === "number" ? state.value : "",
                onBlur: handleBlur,
                onChange: (event) => {
                  const value = event.target.valueAsNumber;

                  handleChange(
                    (Number.isNaN(value) ? undefined : value) as DeepValue<
                      TFormData,
                      TName
                    >,
                  );
                },
              },
            })}
          </InputField>
        );
      }}
    </form.Field>
  );
};
